import { checkClaimsWeb, isItineraryNotesEnabled } from "@eventr/common";
import { useGuestStore } from "feat/guest/guest.store";
import { useViewedUserId } from "feat/viewedUser/viewedUser";
import { useMemo } from "react";
import { BoundStore, useBoundStore } from "state/bound.store";
import { useShallow } from "zustand/react/shallow";
import { listNoteItems } from "./note-items";
import { countOwnDrafts, IAppNote } from "./note-state";

const EMPTY_NOTES: IAppNote[] = [];

/** Whether the selected itinerary shows notes, for code outside React. Guests never do. */
export const selectNotesEnabled = (s: BoundStore): boolean =>
  !useGuestStore.getState().isGuestMode &&
  isItineraryNotesEnabled(
    s.tier,
    s.orgConfig,
    s.itineraryMap?.find((itinerary) => itinerary?.id === s.selectedItinID)?.tags,
    s.orgConfig?.itinTags,
  );

/** Whether the selected itinerary shows notes. Guests never do. */
export const useNotesEnabled = (): boolean => {
  const { tier, orgConfig, tags } = useBoundStore(
    useShallow((s) => ({
      tier: s.tier,
      orgConfig: s.orgConfig,
      tags: s.itineraryMap?.find((itinerary) => itinerary?.id === s.selectedItinID)?.tags,
    })),
  );
  const isGuestMode = useGuestStore((s) => s.isGuestMode);

  return !isGuestMode && isItineraryNotesEnabled(tier, orgConfig, tags, orgConfig?.itinTags);
};

/** Notes are read-only while looking at a team member's itinerary. */
export const useCanWriteNotes = (): boolean => {
  const enabled = useNotesEnabled();
  const viewedUserId = useViewedUserId();
  return enabled && viewedUserId === null && checkClaimsWeb(["/"], ["writeItineraryNotes"]);
};

/**
 * The selected itinerary's notes the viewer may see. On a team member's itinerary that is
 * their published notes, never the viewer's own drafts.
 */
export const useItineraryNotes = (): { notes: IAppNote[]; myDrafts: number; userId: number | undefined } => {
  const { notes, userId } = useBoundStore(
    useShallow((s) => ({
      notes: s.notesByItinerary[s.selectedItinID] ?? EMPTY_NOTES,
      userId: s.wpUser?.id,
    })),
  );
  const viewedUserId = useViewedUserId();

  return useMemo(() => {
    const shown = viewedUserId === null ? notes : notes.filter((note) => note.status === "published");
    return { notes: shown, myDrafts: viewedUserId === null ? countOwnDrafts(notes, userId) : 0, userId };
  }, [notes, userId, viewedUserId]);
};

export const useNoteItems = () => {
  const v2 = useBoundStore((s) => s.selectedItinerary?.v2);
  return useMemo(() => listNoteItems(v2), [v2]);
};
